import { useEffect, useRef } from 'react';
import { Globe, BarChart2, Building2, Briefcase, ArrowRight } from 'lucide-react';

const cards = [
  {
    Icon: Globe,
    iconColor: '#2D5A8C',
    bgColor: '#EEF4FF',
    title: 'The India-UK Tax Corridor',
    description: 'Harmonize HMRC and Indian Income Tax requirements. We eliminate double taxation using DTAA benefits and treaty optimization.',
  },
  {
    Icon: BarChart2,
    iconColor: '#F59E0B',
    bgColor: '#FFF8EE',
    title: 'Global Tax Alignment',
    description: 'Single-window solution for managing income across various jurisdictions. Maximize tax credits and minimize your global tax footprint.',
  },
  {
    Icon: Building2,
    iconColor: '#1B8A96',
    bgColor: '#E8F6F7',
    title: 'Cross-Border Business Setup',
    description: 'Strategic structuring for MSMEs and startups entering India or UK. Optimize tax incentives and scale without compliance burdens.',
  },
  {
    Icon: Briefcase,
    iconColor: '#7C3AED',
    bgColor: '#F5F0FF',
    title: 'Virtual CFO Services',
    description: 'High-level financial strategy without the overhead. Complete ownership of your accounting, payroll, and tax obligations.',
  },
];

const ValueProposition = () => {
  const sectionRef = useRef(null);

  useEffect(() => {
    const cards = sectionRef.current?.querySelectorAll('.value-card');
    if (!cards?.length) return;

    // Set initial invisible state
    cards.forEach((card) => {
      card.style.opacity = '0';
      card.style.transform = 'translateY(32px)';
      card.style.transition = 'opacity 0.7s ease, transform 0.7s ease';
    });

    const io = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting) {
          cards.forEach((card, i) => {
            setTimeout(() => {
              card.style.opacity = '1';
              card.style.transform = 'translateY(0)';
            }, i * 130);
          });
          io.disconnect();
        }
      },
      { threshold: 0.05, rootMargin: '0px 0px -40px 0px' }
    );

    if (sectionRef.current) io.observe(sectionRef.current);
    return () => io.disconnect();
  }, []);

  return (
    <section
      ref={sectionRef}
      data-testid="value-proposition-section"
      id="value-props"
      aria-label="Why choose us for India-UK tax advisory"
      className="py-20 relative overflow-hidden"
      style={{ backgroundColor: '#FAFBFC' }}
    >
      {/* Concentric arcs — top-left origin */}
      <svg
        className="absolute -top-24 -left-24 pointer-events-none"
        width="480" height="480" viewBox="0 0 480 480" fill="none"
        aria-hidden="true"
      >
        {[80, 140, 200, 260, 320, 380].map((r) => (
          <circle key={r} cx="0" cy="0" r={r} stroke="#2D5A8C" strokeWidth="1.5" strokeOpacity="0.07" fill="none" />
        ))}
      </svg>
      {/* Matching arcs — bottom-right origin */}
      <svg
        className="absolute -bottom-24 -right-24 pointer-events-none"
        width="480" height="480" viewBox="0 0 480 480" fill="none"
        aria-hidden="true"
      >
        {[80, 140, 200, 260, 320, 380].map((r) => (
          <circle key={r} cx="480" cy="480" r={r} stroke="#1B8A96" strokeWidth="1.5" strokeOpacity="0.07" fill="none" />
        ))}
      </svg>
      <div className="max-w-6xl mx-auto px-6 relative">
        <div className="text-center mb-14">
          <p className="text-xs font-semibold uppercase tracking-widest mb-3" style={{ color: '#2D5A8C' }}>
            Our Expertise
          </p>
          <h2
            className="text-3xl sm:text-4xl font-bold mb-4"
            style={{ color: '#1F2937', fontFamily: "'Outfit', sans-serif" }}
          >
            Why Choose Us
          </h2>
          <p className="text-base max-w-xl mx-auto" style={{ color: '#6B7280' }}>
            Specialized expertise across India-UK cross-border financial matters — your trusted advisor in both jurisdictions.
          </p>
        </div>

        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
          {cards.map(({ Icon, iconColor, bgColor, title, description }, i) => (
            <div
              key={i}
              data-testid={`value-card-${i}`}
              className="value-card bg-white rounded-2xl p-6 border transition-all duration-300 hover:-translate-y-2 hover:shadow-xl group cursor-pointer"
              style={{ borderColor: '#F3F4F6', boxShadow: '0 2px 12px rgba(0,0,0,0.05)' }}
            >
              <div
                className="w-12 h-12 rounded-xl flex items-center justify-center mb-5 transition-transform duration-300 group-hover:scale-110"
                style={{ backgroundColor: bgColor }}
              >
                <Icon size={22} style={{ color: iconColor }} />
              </div>
              <h3
                className="font-semibold text-base mb-2 leading-tight"
                style={{ color: '#1F2937', fontFamily: "'Outfit', sans-serif" }}
              >
                {title}
              </h3>
              <p className="text-sm leading-relaxed mb-5" style={{ color: '#6B7280' }}>
                {description}
              </p>
              <button
                className="flex items-center gap-1.5 text-xs font-semibold transition-all duration-200 group-hover:gap-2.5"
                style={{ color: iconColor }}
                onClick={() => document.getElementById('services')?.scrollIntoView({ behavior: 'smooth' })}
              >
                Learn More
                <ArrowRight size={13} />
              </button>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default ValueProposition;
